import {
  elapsedTimeContainer,
  formattedDate,
  formattedDuration,
  formattedTime,
} from "./formatted/FormattedDateAndTime.js";
import { ImageCardComponent } from "./imageCard/ImageCardComponent.js";
import ContainerCompact from "../containers/container/ContainerCompact.js";
import ImageListAnomalies from "./ImageListAnomalies.js";
import InfoTip from "./InfoTip.js";
import ActivityDistributionCharts from "./ActivityDistributionCharts.js";
import ActivityLevelChart from "./ActivityLevelChart.js";
import ActivityDetailMetricsPanel from "./ActivityDetailMetricsPanel.js";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "./utils/Date.js";
import { useMemo } from "react";
import { formatMass } from "./utils/formatMass.js";
import { isAdminMode } from "./utils/isAdminMode.js";
import { MIN_HISTORY_DAYS_FOR_PREDICTION } from "../constants/cache";
import { calculateHistoryDays } from "../shared/utils/utils";

const ViewActivity = ({
  dataSortedByAnomalies,
  historyId,
  lastPackmatEfficiency,
  numberOfStars,
  numberOfPackmat,
  totalVolumeSortingError,
  totalWasteVolume,
  totalMassSortingError,
  colorObject,
  labelObjectSeparate,
  dataNumberOfObject,
  numberOfObjects,
  dataAnomalyDelivery,
  labelsAnomalyDelivery,
  colorAnomalyDelivery,
  anomalyChartTotals,
  historyCycle,
  data,
  numberOfAnomaly,
  superContainerDataRow,
  superContainer = null,
  findHistoryId,
  setHistoryId,
  API,
  showMaterialDistributionCharts,
  showDistributionCharts = true,
  showCompactWeightChart = true,
  useDetailHeaderLayout = false,
  showDetailMetrics = true,
  durationFullFillingMs,
  reliablePrediction,
  monitoring,
  fromDate,
  onAfterSegmentationUpdate,
  selectedReadingTime = null,
}) => {
  const { t, i18n } = useTranslation();
  // Fixed size regardless of viewport width: horizontal scroll absorbs any
  // width reduction instead of shrinking/growing the tiles themselves.
  const imageWidth = 140;
  const imageHeight = 140;

  // Calculate maximum filling prediction display
  const maximumFillingDisplay = useMemo(() => {
    if (durationFullFillingMs == null || reliablePrediction !== true) {
      return null;
    }

    const currentDate = new Date();

    // fromDate comes from the backend (historyFillingSpeed endpoint) and represents
    // the start date of the history used to calculate filling speed.
    // The backend may limit this history to a few months even if the container
    // has existed for longer.
    // Calculate history days with UTC normalization to avoid timezone issues
    const historyDays = calculateHistoryDays(fromDate, currentDate);
    // Check if available history is sufficient (>= 30 days)
    // Returns false if historyDays is null (invalid date or future date)
    const hasEnoughHistory =
      historyDays !== null && historyDays >= MIN_HISTORY_DAYS_FOR_PREDICTION;

    if (!hasEnoughHistory) {
      return t("Not enough history for prediction");
    }

    return capitalizeFirstLetter(
      formattedDuration(durationFullFillingMs, i18n.language),
    );
  }, [durationFullFillingMs, reliablePrediction, fromDate, t, i18n.language]);

  const showLevelChartSection =
    (!superContainerDataRow || superContainerDataRow.length === 0) &&
    !data.showDepotCycle &&
    historyCycle?.length > 1;

  // The image card grid normally shares space with distribution charts / the
  // compact level chart. When neither is displayed, let it take the full width.
  const showCompactSection = showLevelChartSection && !useDetailHeaderLayout;
  const imageCardGridIsAlone = !showDistributionCharts && !showCompactSection;
  const isSuperContainerPair =
    Array.isArray(superContainerDataRow) && superContainerDataRow.length > 0;
  const imageCardGridClassName = imageCardGridIsAlone
    ? "grid grid-cols-1 gap-3 w-full"
    : "grid grid-cols-2 gap-3 md:grid-cols-1 sm:grid-cols-1 w-full";

  const superContainerChildContainers = useMemo(() => {
    if (!isSuperContainerPair) return [];
    return superContainerDataRow.map((item) => ({
      ...item,
      _measurement: item._measurement ?? item._id,
    }));
  }, [isSuperContainerPair, superContainerDataRow]);

  const resolvedSuperContainer = useMemo(() => {
    if (!isSuperContainerPair) return null;
    if (superContainer?.containerObjectIdList?.length) {
      return superContainer;
    }
    return {
      _id: data?._id,
      containerObjectIdList: superContainerChildContainers.map(
        (item) => item._id,
      ),
    };
  }, [
    data?._id,
    isSuperContainerPair,
    superContainer,
    superContainerChildContainers,
  ]);

  const superContainerPrimary = superContainerChildContainers[0] || null;

  return (
    <div className="modal-nav-item">
      {useDetailHeaderLayout && showDetailMetrics && !monitoring && (
        <div className="grid grid-cols-3 gap-3 md:grid-cols-1 w-full mb-[15px] items-stretch">
          {showLevelChartSection && (
            <>
              <div className="col-span-2 md:col-span-1 char-section-header char-section-header--general-info !mb-0 min-w-0">
                <h2 className="char-section-header-title">{t("Level")}</h2>
              </div>
              <div className="col-span-1 hidden md:block" aria-hidden="true" />
              <article className="col-span-2 md:col-span-1 bg-white border border-gray-300 rounded-xl w-full min-w-0 flex flex-col self-stretch">
                <ActivityLevelChart
                  container={data}
                  historyCycle={historyCycle}
                  findHistoryId={findHistoryId}
                />
              </article>
            </>
          )}
          <div
            className={
              showLevelChartSection
                ? "col-span-1 md:col-span-1 flex flex-col self-stretch min-h-0 min-w-0"
                : "col-span-3 md:col-span-1 flex flex-col self-stretch min-h-0 min-w-0"
            }
          >
            <ActivityDetailMetricsPanel
              t={t}
              i18n={i18n}
              data={data}
              historyCycle={historyCycle}
              numberOfAnomaly={numberOfAnomaly}
              numberOfStars={numberOfStars}
              totalVolumeSortingError={totalVolumeSortingError}
              totalWasteVolume={totalWasteVolume}
              totalMassSortingError={totalMassSortingError}
              numberOfPackmat={numberOfPackmat}
              lastPackmatEfficiency={lastPackmatEfficiency}
            />
          </div>
        </div>
      )}
      {(!useDetailHeaderLayout || monitoring) && (
        <section className="modal-item-key">
          {monitoring &&
          (data.activatedFillingLevelOption ||
            data.characterization ||
            data.qualicheck) ? (
            <>
              <article className="modal-item-key-content">
                <div className="modal-item-key-title">
                  <span>{t("Filling level")}</span>
                </div>
                {historyCycle.length > 0 ? (
                  <div className="modal-item-key-value">
                    <h5 className="text-2xl">
                      {historyCycle[0].filling_level}%
                    </h5>
                    <div>
                      <span>{formattedDate(historyCycle[0]._time, t)}</span>
                      <span>{formattedTime(historyCycle[0]._time)}</span>
                    </div>
                  </div>
                ) : null}
              </article>
              <article className="modal-item-key-content">
                <div className="modal-item-key-title">
                  <span>{t("Maximum filling in")}</span>
                  <InfoTip>
                    {t(
                      "Estimation of the container's maximum fill based on the average filling rate observed over the last 3 months",
                    )}
                    .
                  </InfoTip>
                </div>
                <div className="modal-item-key-value">
                  {historyCycle.length > 0 &&
                  historyCycle[0].filling_level === 100 ? (
                    <h5 className="text-2xl">{t("Maximum level reached")}</h5>
                  ) : (
                    <>
                      {maximumFillingDisplay ? (
                        <h5 className="text-2xl">{maximumFillingDisplay}</h5>
                      ) : (
                        <div className="flex flex-row items-center">
                          <span className="text-2xl text-[#e83e8c]">-</span>
                          {durationFullFillingMs == null && (
                            <span>{`(${t("Please configure your opening hours in 'Address'")})`}</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </article>
              <article className="modal-item-key-content">
                <div className="modal-item-key-title">
                  <span>{t("Filling speed")}</span>
                  <InfoTip>
                    {t(
                      "Average filling speed per calendar day (includes any packmat)",
                    )}
                    .
                  </InfoTip>
                </div>
                <div className="modal-item-key-value">
                  {data?.meanFillingLevelByDay !== undefined ? (
                    <h5 className="text-2xl">
                      {Math.round(data.meanFillingLevelByDay * 10) / 10}% /
                      <span>{t("Day")}</span>
                    </h5>
                  ) : (
                    <h5 className="text-2xl">-</h5>
                  )}
                  {data?.meanFillingLevelByOpenedHour !== undefined ? (
                    <div>
                      <span>
                        {Math.round(data.meanFillingLevelByOpenedHour * 10) /
                          10}
                        % /{` ${t("opening hour")}`}
                      </span>
                    </div>
                  ) : null}
                </div>
              </article>
            </>
          ) : (
            !useDetailHeaderLayout && (
              <article className="modal-item-key-content">
                <div className="modal-item-key-title">
                  <span>Cycle</span>
                  <InfoTip>
                    {t(
                      "Number of days between drop-off date and collection date",
                    )}
                    .
                  </InfoTip>
                </div>
                <div className="modal-item-key-value">
                  <h5>
                    {historyCycle.length > 0
                      ? elapsedTimeContainer(
                          historyCycle[historyCycle.length - 1]._time,
                          i18n.language,
                          false,
                          new Date(data._time),
                        )
                      : "-"}
                  </h5>
                  <div>
                    <span>
                      {t("Drop-off")} :{" "}
                      {formattedDate(
                        historyCycle[historyCycle.length - 1]._time,
                        t,
                      )}
                      ,{" "}
                      {formattedTime(
                        historyCycle[historyCycle.length - 1]?._time,
                        i18n.language,
                      )}
                    </span>
                    <span>
                      {historyCycle.length > 1 &&
                        (data.showDepotCycle === undefined ||
                          !data.showDepotCycle) &&
                        `${t("Collection")}: ${formattedDate(data._time, t)}, ${formattedTime(data._time, i18n.language)}`}
                    </span>
                  </div>
                </div>
              </article>
            )
          )}
          {!monitoring && !useDetailHeaderLayout && (
            <article className="modal-item-key-content">
              <div className="modal-item-key-title">
                <span>{t("Number of sorting errors")}</span>
                <InfoTip>
                  {t(
                    "Anomalies are counted once, even if objects appear multiple times.",
                  )}
                </InfoTip>
              </div>
              <div className="modal-item-key-value">
                <h5>
                  {data.activatedSortingErrorOption
                    ? numberOfAnomaly
                      ? Array.isArray(numberOfAnomaly)
                        ? numberOfAnomaly.reduce(
                            (sum, val) => sum + (val || 0),
                            0,
                          )
                        : numberOfAnomaly
                      : 0
                    : ""}
                </h5>
              </div>
            </article>
          )}

          {(!useDetailHeaderLayout || monitoring) && (
            <article className="modal-item-key-content">
              <div className="modal-item-key-title">
                <span>{t("Waste category quality")}</span>

                <InfoTip widthMaxRem={16}>
                  <p className="m-0 text-[13px] leading-tight opacity-90">
                    {t(
                      "Assessment of material quality based on the volumetric proportion of detected errors",
                    )}
                    .
                  </p>

                  <ul className="mt-1 space-y-0.5 text-[13px] leading-none">
                    <li className="flex items-center gap-1.5">
                      <span className="whitespace-nowrap">
                        <i className="far fa-star text-[13px] align-middle"></i>
                        <i className="far fa-star text-[13px] ml-0.5"></i>
                        <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
                      </span>
                      <span className="text-[13px] opacity-90 whitespace-nowrap">
                        {t("More than 30% of contaminants")}
                      </span>
                    </li>

                    <li className="flex items-center gap-1.5">
                      <span className="whitespace-nowrap">
                        <i className="fas fa-star text-[13px] align-middle"></i>
                        <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
                        <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
                      </span>
                      <span className="text-[13px] opacity-90 whitespace-nowrap">
                        {t("Between 15% and 30% of contaminants")}
                      </span>
                    </li>

                    <li className="flex items-center gap-1.5">
                      <span className="whitespace-nowrap">
                        <i className="fas fa-star text-[13px] align-middle"></i>
                        <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
                        <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
                      </span>
                      <span className="text-[13px] opacity-90 whitespace-nowrap">
                        {t("Between 5% and 15% of contaminants")}
                      </span>
                    </li>

                    <li className="flex items-center gap-1.5">
                      <span className="whitespace-nowrap">
                        <i className="fas fa-star text-[13px] align-middle"></i>
                        <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
                        <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
                      </span>
                      <span className="text-[13px] opacity-90 whitespace-nowrap">
                        {t("Less than 5% of contaminants")}
                      </span>
                    </li>
                  </ul>
                </InfoTip>
              </div>
              <div className="flex flex-row modal-item-key-value">
                {data.activatedSortingErrorOption && (
                  <div className="flex flex-row w-full justify-between">
                    <article className="modal-item-key-star">
                      {numberOfStars === 3 ? (
                        <>
                          <i className="fas fa-star"></i>
                          <i className="fas fa-star"></i>
                          <i className="fas fa-star"></i>
                        </>
                      ) : null}
                      {numberOfStars === 2 ? (
                        <>
                          <i className="fas fa-star"></i>
                          <i className="fas fa-star"></i>
                          <i className="far fa-star"></i>
                        </>
                      ) : null}
                      {numberOfStars === 1 ? (
                        <>
                          <i className="fas fa-star"></i>
                          <i className="far fa-star"></i>
                          <i className="far fa-star"></i>
                        </>
                      ) : null}
                      {numberOfStars === 0 ? (
                        <>
                          <i className="far fa-star"></i>
                          <i className="far fa-star"></i>
                          <i className="far fa-star"></i>
                        </>
                      ) : null}
                    </article>
                    <span className="mt-4">
                      {totalVolumeSortingError > 0
                        ? (totalVolumeSortingError * 100) / totalWasteVolume < 1
                          ? "<1"
                          : Math.round(
                              (totalVolumeSortingError * 100) /
                                totalWasteVolume,
                            )
                        : 0}
                      % {t("of contaminants")}
                      {isAdminMode() &&
                        totalMassSortingError != null &&
                        totalMassSortingError > 0 && (
                          <> - {formatMass(totalMassSortingError)}</>
                        )}
                    </span>
                  </div>
                )}
              </div>
            </article>
          )}

          {(!useDetailHeaderLayout || monitoring) && data.packmatActive ? (
            <article className="modal-item-key-content">
              <div className="modal-item-key-title">
                <span>{t("Packmat")}</span>
                <InfoTip>
                  {t(
                    "Number of packmat carried out since the arrival of the container on site and efficiency of the last packmat",
                  )}
                  .
                </InfoTip>
              </div>
              <div className="modal-item-key-value">
                <h5>{numberOfPackmat}</h5>

                {lastPackmatEfficiency ? (
                  <div>
                    <span>
                      {formattedDate(lastPackmatEfficiency._time, t)} -{" "}
                      {formattedTime(lastPackmatEfficiency._time)}
                    </span>

                    <span>
                      {t("Efficiency")}:{" "}
                      {lastPackmatEfficiency
                        ? lastPackmatEfficiency.efficiency
                        : "-"}
                      %
                    </span>
                  </div>
                ) : null}
              </div>
            </article>
          ) : null}
        </section>
      )}
      {historyCycle.length > 1 &&
        data.activatedSortingErrorOption &&
        dataSortedByAnomalies.length > 0 && (
          <ImageListAnomalies
            container={data}
            dataSortedByAnomalies={dataSortedByAnomalies}
            findHistoryId={findHistoryId}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            historyCycle={historyCycle}
            selectedReadingTime={
              selectedReadingTime ?? historyCycle?.[historyId]?._time ?? null
            }
          />
        )}
      {isSuperContainerPair &&
      historyCycle.length > 0 &&
      superContainerPrimary &&
      resolvedSuperContainer ? (
        <section className="flex flex-col w-full gap-3 mb-3">
          <div className="char-section-header char-section-header--waste-ai flex flex-row flex-wrap gap-3 !justify-start !mb-0">
            <h2 className="char-section-header-title !mb-0">
              {t("Waste analyzed by AI")}
            </h2>
          </div>
          <article className="bg-white border border-gray-300 rounded-xl w-full p-3">
            <ImageCardComponent
              container={superContainerPrimary}
              containers={superContainerChildContainers}
              superContainer={resolvedSuperContainer}
              displayLegend={false}
              history={historyCycle}
              historyId={historyId}
              setHistoryId={setHistoryId}
              isCharacterization
              isActivity={false}
              displayAllCountours
              displayAnomalyLegend={false}
              hideShowNewObjectsButton
              hideSizeRangeDropdown
              sortingErrorsMaterialsOnly
              materialsPillLabelKey="Sorting errors"
              materialsSelectEmptyLabelKey="select_sorting_errors"
              onAfterSegmentationUpdate={onAfterSegmentationUpdate}
              isQualicheck={data.qualicheck || false}
            />
          </article>
        </section>
      ) : null}
      <div className={imageCardGridClassName}>
        {!isSuperContainerPair &&
          historyCycle.length > 0 &&
          [data].map((item, index) => (
            <article
              key={item._id || item._time || index}
              className="bg-white border border-gray-300 rounded-xl w-full min-w-0 pb-4 flex flex-col items-stretch"
            >
              <ImageCardComponent
                API={API}
                container={item}
                displayLegend={true}
                history={historyCycle}
                historyId={historyId}
                isCharacterization={false}
                setHistoryId={setHistoryId}
                displayAnomalyLegend={false}
                isActivity={true}
                containerActivity={true}
                onAfterSegmentationUpdate={onAfterSegmentationUpdate}
                isQualicheck={data.qualicheck || false}
              />
            </article>
          ))}
        {showDistributionCharts && (
          <ActivityDistributionCharts
            activatedSortingErrorOption={data.activatedSortingErrorOption}
            dataAnomalyDelivery={dataAnomalyDelivery}
            labelsAnomalyDelivery={labelsAnomalyDelivery}
            colorAnomalyDelivery={colorAnomalyDelivery}
            numberOfAnomaly={numberOfAnomaly}
            anomalyChartTotals={anomalyChartTotals}
            showMaterialDistributionCharts={showMaterialDistributionCharts}
            dataNumberOfObject={dataNumberOfObject}
            labelObjectSeparate={labelObjectSeparate}
            numberOfObjects={numberOfObjects}
            colorObject={colorObject}
          />
        )}
        {showCompactSection && (
          <article className="bg-white rounded-xl w-full truncate">
            <ContainerCompact
              container={data}
              history={true}
              dataForCompactOverlay={historyCycle}
              findHistoryId={findHistoryId}
              showPackmat={true}
              showLimitFillingLevel={true}
              showToolbox={true}
              showWeightChart={showCompactWeightChart}
            />
          </article>
        )}
      </div>
    </div>
  );
};

export default ViewActivity;
